import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Badge,
  Box,
  Button,
  Card as RadixCard,
  Flex,
  Grid,
  Heading,
  IconButton,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";
import PageTemplate from "@/components/page-template/PageTemplate";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { SkeletonPage } from "@/components/ui/Skeleton";
import ErrorCard from "@/components/ui/ErrorCard";
import SideDrawer from "@/components/ui/SideDrawer";
import { BasicTable, Column } from "@/components/ui/BasicTable";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { useRoles, useUsers } from "@/hooks/useCommonQueries";
import { groupService } from "@/services/breeze/GroupService";
import { roleAndPermissionService } from "@/services/breeze/RoleAndPermissionService";
import {
  AddGroupMemberRequest,
  Group,
  GroupMember,
  UpdateGroupMemberRequest,
  UpdateGroupRequest,
} from "@/types/breeze/Group";

import { useAuth } from "@/providers/AuthProvider";
import { useConfirm } from "@/providers/ConfirmDialogProvider";

const EditGroupPage = withTenantAdminRequired(function EditGroupPage() {
  const params = useParams();
  const groupId = parseInt(params.groupId as string, 10);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentTenant, safeNavigate } = useAuth();
  const [isMemberDrawerOpen, setIsMemberDrawerOpen] = useState(false);

  const {
    data: group,
    isLoading: isGroupLoading,
    error: groupError,
  } = useQuery<Group>({
    queryKey: ["group", groupId],
    queryFn: () => groupService.getGroup(groupId),
  });

  const {
    data: members = [],
    isLoading: isMembersLoading,
  } = useQuery<GroupMember[]>({
    queryKey: ["group-members", groupId],
    queryFn: () => groupService.listMembers(groupId),
  });

  const { data: users, isLoading: isUsersLoading } = useUsers();
  const { data: roles = [] } = useRoles();

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: String(r.id), label: r.name })),
    [roles],
  );

  const updateGroup = useMutation({
    mutationFn: (request: UpdateGroupRequest) =>
      groupService.updateGroup(groupId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group", groupId] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const invalidateMembers = () => {
    queryClient.invalidateQueries({ queryKey: ["group-members", groupId] });
    queryClient.invalidateQueries({ queryKey: ["user-groups"] });
  };

  const addMember = useMutation({
    mutationFn: (request: AddGroupMemberRequest) =>
      groupService.addMember(groupId, request),
    onSuccess: () => {
      invalidateMembers();
      setIsMemberDrawerOpen(false);
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: number) => groupService.removeMember(groupId, memberId),
    onSuccess: invalidateMembers,
  });

  const updateMember = useMutation({
    mutationFn: ({ memberId, request }: { memberId: number; request: UpdateGroupMemberRequest }) =>
      groupService.updateMember(groupId, memberId, request),
    onSuccess: invalidateMembers,
  });

  const availableUsers = useMemo(
    () =>
      users?.filter((u) => !members.some((m) => m.user_id === u.id)) ?? [],
    [users, members],
  );

  if (isGroupLoading) return <SkeletonPage title="Group" />;
  if (groupError || !group) return <ErrorCard error="Failed to load group" />;

  const memberColumns: Column<GroupMember>[] = [
    {
      header: "User",
      accessor: "user",
      cellRenderer: (m) =>
        `${m.user.first_name} ${m.user.last_name} (${m.user.email})`,
    },
    {
      header: "Role",
      accessor: "role",
      cellRenderer: (m) => (
        <Flex align="center" gap="2">
          <Box style={{ minWidth: 200 }}>
            <SearchableSelect
              value={m.role_id ? String(m.role_id) : ""}
              options={roleOptions}
              placeholder="Select a role"
              disabled={updateMember.isPending}
              onChange={(value) =>
                updateMember.mutate({
                  memberId: m.id,
                  request: { role_id: Number(value) },
                })
              }
            />
          </Box>
          {m.can_read_group_data && (
            <Badge color="amber" variant="soft" title="This role can read the whole group's data">
              Staff
            </Badge>
          )}
        </Flex>
      ),
    },
    {
      header: "Joined",
      accessor: "joined_at",
      cellRenderer: (m) => new Date(m.joined_at).toLocaleDateString(),
    },
    {
      header: "Actions",
      accessor: "id",
      cellRenderer: (m) => (
        <IconButton
          size="1"
          variant="ghost"
          color="red"
          onClick={async () => {
            const confirmed = await confirm({
              title: "Remove member",
              message: `Remove ${m.user.first_name} ${m.user.last_name} from ${group.name}?`,
              confirmText: "Remove",
              cancelText: "Cancel",
              variant: "danger",
            });
            if (confirmed) removeMember.mutate(m.id);
          }}
          title="Remove member"
        >
          <Trash2 size={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <PageTemplate title={group.name}>
      <Flex direction="column" gap="4">
        <RadixCard>
          <Box p="4">
            <Heading size="4" mb="3">
              Group details
            </Heading>
            <Formik
              enableReinitialize
              initialValues={{
                name: group.name,
                description: group.description ?? "",
                owner_user_id: group.owner_user_id ?? 0,
              }}
              validationSchema={Yup.object({
                name: Yup.string().required("Name is required"),
                description: Yup.string(),
              })}
              onSubmit={(values) =>
                updateGroup.mutate({
                  name: values.name,
                  description: values.description || undefined,
                  owner_user_id: values.owner_user_id
                    ? values.owner_user_id
                    : undefined,
                })
              }
            >
              <Form>
                <Grid columns="2" gap="4">
                  <InputField name="name" label="Name" required />
                  <InputField name="description" label="Description" />
                  <SelectField
                    name="owner_user_id"
                    label="Owner"
                    type="number"
                    disabled={isUsersLoading}
                  >
                    <SelectField.Item value={0}>No owner</SelectField.Item>
                    {(users ?? []).map((u) => (
                      <SelectField.Item key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({u.email})
                      </SelectField.Item>
                    ))}
                  </SelectField>
                </Grid>
                <Flex justify="end" mt="4">
                  <Button type="submit" loading={updateGroup.isPending}>
                    Save changes
                  </Button>
                </Flex>
              </Form>
            </Formik>
          </Box>
        </RadixCard>

        {/* contain: paint would make this Card the containing block for the role popover and clip it */}
        <RadixCard style={{ contain: "none" }}>
          <Box p="4">
            <Flex justify="between" align="center" mb="3">
              <Heading size="4">Members</Heading>
              <Button
                size="2"
                onClick={() => setIsMemberDrawerOpen(true)}
                disabled={availableUsers.length === 0}
              >
                <Plus size={16} />
                Add member
              </Button>
            </Flex>

            {members.length === 0 ? (
              <Box py="8">
                <Text align="center" color="gray" as="p">
                  No members yet
                </Text>
              </Box>
            ) : (
              <BasicTable
                columns={memberColumns}
                data={members}
                isLoading={isMembersLoading}
              />
            )}
          </Box>
        </RadixCard>

        <Flex justify="end">
          <Button
            variant="soft"
            color="red"
            onClick={async () => {
              const confirmed = await confirm({
                title: "Delete Group",
                message: `Delete "${group.name}"? All members and RLS grants for this group will also be removed.`,
                confirmText: "Delete",
                cancelText: "Cancel",
                variant: "danger",
              });
              if (confirmed) {
                await groupService.deleteGroup(groupId);
                queryClient.invalidateQueries({ queryKey: ["groups"] });
                queryClient.invalidateQueries({ queryKey: ["user-groups"] });
                safeNavigate(`/org/${currentTenant?.id}/settings/groups`);
              }
            }}
            disabled={group.is_system}
          >
            Delete group
          </Button>
        </Flex>
      </Flex>

      <SideDrawer
        isOpen={isMemberDrawerOpen}
        onClose={() => setIsMemberDrawerOpen(false)}
        title="Add member"
      >
        <Box p="4">
          {availableUsers.length === 0 ? (
            <Box py="8">
              <Text align="center" color="gray" as="p">
                All users are already members of this group
              </Text>
            </Box>
          ) : (
            <Formik
              initialValues={{ user_id: 0, role_id: 0 }}
              validationSchema={Yup.object({
                user_id: Yup.number().min(1, "Select a user").required(),
                role_id: Yup.number().min(1, "Select a role").required(),
              })}
              onSubmit={(values) =>
                addMember.mutate({
                  user_id: values.user_id,
                  role_id: values.role_id,
                })
              }
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Flex direction="column" gap="4">
                    <SelectField
                      name="user_id"
                      label="User"
                      type="number"
                      required
                    >
                      <SelectField.Item value={0}>Select a user</SelectField.Item>
                      {availableUsers.map((u) => (
                        <SelectField.Item key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </SelectField.Item>
                      ))}
                    </SelectField>
                    <SelectField
                      name="role_id"
                      label="Role"
                      type="number"
                      required
                      description="The member's platform role. A role that can update groups also reads the whole group's data."
                    >
                      <SelectField.Item value={0}>Select a role</SelectField.Item>
                      {roles.map((r) => (
                        <SelectField.Item key={r.id} value={r.id}>
                          {r.name}
                        </SelectField.Item>
                      ))}
                    </SelectField>
                    <CreateRoleInline
                      onCreated={(roleId) => setFieldValue("role_id", roleId)}
                    />
                    <Grid columns="2" gap="4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsMemberDrawerOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={!values.user_id || !values.role_id || addMember.isPending}
                      >
                        {addMember.isPending ? "Adding..." : "Add member"}
                      </Button>
                    </Grid>
                  </Flex>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </SideDrawer>
    </PageTemplate>
  );
});

export default EditGroupPage;

// The new role starts with no permissions — it is configured under Roles & permissions afterwards.
function CreateRoleInline({ onCreated }: { onCreated: (roleId: number) => void }) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");

  const createRole = useMutation({
    mutationFn: () =>
      roleAndPermissionService.createRole({
        name: name.trim(),
        description: "",
        frontend_url: null,
        is_active: true,
        anyapi_access: false,
        permission_ids: [],
      }),
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      onCreated(role.id);
      setName("");
      setIsOpen(false);
    },
  });

  if (!isOpen) {
    return (
      <Flex justify="start">
        <Button type="button" size="1" variant="ghost" onClick={() => setIsOpen(true)}>
          <Plus size={14} />
          Create a new role
        </Button>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2">
      <Text size="1" color="gray">
        New role name — it starts with no permissions, set those under Roles &amp; permissions
      </Text>
      <Flex gap="2">
        <TextField.Root
          value={name}
          placeholder="e.g. team_lead"
          style={{ flexGrow: 1 }}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              if (name.trim()) createRole.mutate();
            }
          }}
        />
        <Button
          type="button"
          size="2"
          disabled={!name.trim() || createRole.isPending}
          onClick={() => createRole.mutate()}
        >
          {createRole.isPending ? "Creating..." : "Create"}
        </Button>
        <Button type="button" size="2" variant="soft" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
}
